import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {
    Box,
    Accordion,
    AccordionItem,
    useToast
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useForm} from 'react-hook-form'
import PromoCodeFields from '../forms/promo-code-fields'
import {API_ERROR_MESSAGE} from '@salesforce/retail-react-app/app/constants'
import {useShopperBasketsMutation} from '@salesforce/commerce-sdk-react'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {pxToRem} from '../../utills/pxToRem'
import {
    extractCouponsFromBasket,
    getCouponFeedbackMessage,
    pushCouponEvent
} from '../../utills/couponUtils'

export const usePromoCode = () => {
    const {formatMessage} = useIntl()
    const {data: basket} = useCurrentBasket()
    const form = useForm()
    const toast = useToast()

    const applyPromoCodeMutation = useShopperBasketsMutation('addCouponToBasket')
    const removePromoCodeMutation = useShopperBasketsMutation('removeCouponFromBasket')

    const findCouponByCode = (coupons, code) =>
        coupons.find((coupon) => coupon.couponCode?.toLowerCase() === code.toLowerCase())

    const getCouponItemId = (basketData, code) =>
        basketData?.couponItems?.find((item) => item.code?.toLowerCase() === code.toLowerCase())
            ?.couponItemId

    const removeCouponQuietly = async (basketId, couponItemId) => {
        if (!basketId || !couponItemId) return
        try {
            await removePromoCodeMutation.mutateAsync({
                parameters: {basketId, couponItemId}
            })
        } catch {
            // Cleanup is best-effort
        }
    }

    const resetCodeField = () => {
        form.reset(
            {code: ''},
            {
                keepErrors: false,
                keepDirty: false,
                keepIsSubmitSuccessful: false
            }
        )
    }

    const showCodeError = (message) => {
        form.setError(
            'code',
            {
                type: 'manual',
                message
            },
            {shouldFocus: true}
        )
    }

    const submitPromoCode = async ({code}, products) => {
        const basketId = basket?.basketId

        try {
            const currentCoupons = extractCouponsFromBasket(basket)
            const existingCoupon = findCouponByCode(currentCoupons, code)

            if (existingCoupon?.isApplied) {
                showCodeError(
                    formatMessage({
                        defaultMessage: 'This promo code is already applied',
                        id: 'use_promocode.error.already_applied'
                    })
                )
                return
            }

            if (existingCoupon) {
                await removeCouponQuietly(
                    basketId,
                    existingCoupon.couponItemId || getCouponItemId(basket, code)
                )
            }

            const updatedBasket = await applyPromoCodeMutation.mutateAsync({
                parameters: {basketId},
                body: {
                    code
                }
            })

            resetCodeField()

            const resultCoupon = findCouponByCode(extractCouponsFromBasket(updatedBasket), code)

            if (!resultCoupon || !resultCoupon.isApplied) {
                const statusCode = resultCoupon?.statusCode
                await removeCouponQuietly(
                    updatedBasket?.basketId || basketId,
                    resultCoupon?.couponItemId || getCouponItemId(updatedBasket, code)
                )
                showCodeError(
                    getCouponFeedbackMessage({
                        statusCode,
                        code,
                        formatMessage
                    })
                )
                return
            }

            pushCouponEvent(resultCoupon.couponItemId, updatedBasket, products, true)

            toast({
                title: formatMessage({
                    defaultMessage: 'Promotion applied',
                    id: 'use_promocode.info.promo_applied'
                }),
                status: 'success',
                position: 'top-right',
                isClosable: true
            })
        } catch (e) {
            const lingeringCoupon = findCouponByCode(extractCouponsFromBasket(basket), code)
            const statusCode =
                lingeringCoupon && !lingeringCoupon.isApplied
                    ? lingeringCoupon.statusCode
                    : undefined

            if (lingeringCoupon && !lingeringCoupon.isApplied) {
                await removeCouponQuietly(
                    basketId,
                    lingeringCoupon.couponItemId || getCouponItemId(basket, code)
                )
            }

            resetCodeField()
            showCodeError(
                getCouponFeedbackMessage({
                    statusCode,
                    code,
                    formatMessage
                })
            )
        }
    }

    const removePromoCode = async (couponItemId, products) => {
        const currentBasket = basket
        removePromoCodeMutation.mutate(
            {
                parameters: {basketId: basket?.basketId, couponItemId}
            },
            {
                onSuccess: () => {
                    if (form?.formState?.errors?.code) {
                        form.clearErrors('code')
                    }
                    if (products) {
                        pushCouponEvent(couponItemId, currentBasket, products, false)
                    }

                    toast({
                        title: formatMessage({
                            defaultMessage: 'Promotion removed',
                            id: 'use_promocode.info.promo_removed'
                        }),
                        status: 'success',
                        position: 'top-right',
                        isClosable: true
                    })
                },
                onError: () => {
                    toast({
                        title: formatMessage(API_ERROR_MESSAGE),
                        status: 'error',
                        position: 'top-right',
                        isClosable: true
                    })
                }
            }
        )
    }

    return {form, basket, submitPromoCode, removePromoCode}
}

export const PromoCode = ({form, submitPromoCode, removePromoCode, products, itemProps}) => {
    const [isOpen, setOpen] = useState()
    const {data: basket} = useCurrentBasket()

    useEffect(() => {
        if (form.formState.isSubmitSuccessful) {
            setOpen(false)
        }
    }, [form.formState.isSubmitSuccessful])

    return (
        <Accordion allowToggle index={isOpen ? 0 : -1} onChange={() => setOpen(!isOpen)}>
            <AccordionItem {...itemProps}>
                <>
                    <Box
                        data-testid="promo-code-form"
                        as="form"
                        onSubmit={form.handleSubmit((data) => submitPromoCode(data, products))}
                    >
                        <PromoCodeFields
                            form={form}
                            marginBottom={pxToRem(30)}
                            basket={basket}
                            removePromoCode={removePromoCode}
                            products={products}
                        />
                    </Box>
                </>
            </AccordionItem>
        </Accordion>
    )
}

PromoCode.propTypes = {
    /** The form object returned from `usePromoCode` hook */
    form: PropTypes.object.isRequired,

    /** The submit callback returned from `usePromoCode` hook */
    submitPromoCode: PropTypes.func.isRequired,

    /** The remove promo code callback returned from `usePromoCode` hook */
    removePromoCode: PropTypes.func.isRequired,

    products: PropTypes.any,

    /** Props applied to inner AccordionItem. Useful for style overrides. */
    itemProps: PropTypes.object
}

export default PromoCode
